import _ from "lodash";
import { AssetViewTypes } from "../../features/assetsunderadvicegrid/hooks/AssetsUnderAdviceContext";
import { wealthProApi } from "../../redux/api";
import { useCacheKeySerializeQueryArgsHandler, useCacheKeyTransformResponseHandler } from "../../redux/helpers";

const ViewTypeEndpointMap = {
    [AssetViewTypes.FundsSummary]: "fund",
    [AssetViewTypes.ClientsSummary]: "client",
    [AssetViewTypes.HoldingsSummary]: "holding"
}

const allClientValuationsApiEndpoints = wealthProApi.enhanceEndpoints({
}).injectEndpoints({
    endpoints: (builder) => ({
        fetchAllClientValuationBuilds: builder.query({
            query: ({ valuationDate }) => `api/valuations/allclientvaluations/${valuationDate}`
        }),
        searchAllClientValuations: builder.query({
            query: ({ buildId, viewType, ...body }) => ({
                url: `api/valuations/allclientvaluations/${buildId}/search/${ViewTypeEndpointMap[viewType]}`,
                method: "POST",
                body
            }),
            transformResponse: useCacheKeyTransformResponseHandler,
            serializeQueryArgs: useCacheKeySerializeQueryArgsHandler,
            // Need to re-implement the merge handler to get the total value to map across properly
            merge: (currentCache, newItems) => {
                if (_.isEqual(currentCache.cacheKey, newItems.cacheKey)) {
                    currentCache.pagination = newItems.pagination;
                    currentCache.results.push(...newItems.results);
                    // totalValue only calculated on first page load, so ignore it when cacheKey is unchanged
                }
                else {
                    currentCache.newRecordsSinceCache = 0;
                    currentCache.cacheKey = newItems.cacheKey;
                    currentCache.pagination = newItems.pagination;
                    currentCache.results = [...newItems.results];
                    currentCache.totalValue = newItems.totalValue;
                }
            }
        }),
        buildAllClientValuations: builder.mutation({
            query: ({ valuationDate }) => ({
                url: `api/valuations/allclientvaluations/${valuationDate}`,
                method: "POST"
            }),
            async onQueryStarted({ valuationDate }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    dispatch(wealthProApi.util.updateQueryData("fetchAllClientValuationBuilds", { valuationDate }, (draft) => ({
                        ...draft,
                        canRun: false
                    })));
                } catch (error) {
                    console.error("Error creating build", error);
                }
            }
        }),
        persistAllClientValuationsBuild: builder.mutation({
            query: ({ buildId }) => ({
                url: `api/valuations/allclientvaluations/${buildId}/persist`,
                method: "POST"
            }),
            async onQueryStarted({ valuationDate, buildId }, { dispatch, queryFulfilled }) {
                try {
                    const updateResult = dispatch(wealthProApi.util.updateQueryData("fetchAllClientValuationBuilds", { valuationDate }, (draft) => {
                        const index = draft.builds.findIndex(x => x.buildId === buildId);
                        const elt = draft.builds[index];

                        return ({
                            ...draft,
                            builds: [
                                ...draft.builds.slice(0, index),
                                {
                                    ...elt,
                                    data: {
                                        ...elt.data,
                                        isPersistent: true
                                    }
                                }
                            ]
                        });
                    }))

                    try {
                        await queryFulfilled;
                    } catch (error) {
                        console.error(error);
                        console.error("Error updating valuation build table");
                        updateResult.undo();
                    }
                } catch (error) {
                    console.error(error);
                    console.error("Error updating valuation build cache")
                }
            }
        }),
        exportAllClientValuationsBuild: builder.query({
            query: ({ buildId, csv = false, viewType, ...body }) => ({
                url: `api/valuations/allclientvaluations/${buildId}/export/${ViewTypeEndpointMap[viewType]}?csv=${csv}`,
                method: "POST",
                body,
                responseHandler: async (response) => {
                    const blob = await response.blob();
                    const disposition = response.headers.get('content-disposition');
                    let filename = "AllClientValuationsExport.xls";

                    const fileNameStarRegex = /(?<=filename\*=UTF-8'')([^;]+)/i;
                    const matchesStar = disposition.match(fileNameStarRegex);

                    if (matchesStar && matchesStar[1]) {
                        filename = decodeURIComponent(matchesStar[1].trim());
                    } else {
                        console.error("Cannot extract filename");
                    }

                    return { blob, filename };
                },
                cache: 'no-cache'
            })
        }),
        createClientSetFromFilters: builder.mutation({
            query: ({ buildId, ...request }) => ({
                url: `api/valuations/allclientvaluations/${buildId}/set`,
                method: 'POST',
                body: request
            })
        })
    })
});

export const {
    useLazyFetchAllClientValuationBuildsQuery,
    useFetchAllClientValuationBuildsQuery,
    useLazySearchAllClientValuationsQuery,
    useBuildAllClientValuationsMutation,
    usePersistAllClientValuationsBuildMutation,
    useLazyExportAllClientValuationsBuildQuery,
    useCreateClientSetFromFiltersMutation
} = allClientValuationsApiEndpoints;